import {
  emptyPpfpPackageFilters,
  emptyPpfpTaskFilters,
  normalizePpfpPackageFilters,
  normalizePpfpTaskFilters,
  PpfpAction,
  PpfpActions,
  PpfpActionTypes,
  PpfpPackageFilters,
  PpfpTaskFilters,
  ChangeProject,
  ProjectActionTypes,
  ProjectsLoadSuccess,
} from '@completion/actions';
import {
  PpfpTask,
  ProcurementPackage,
  Ppfp,
  PpfpActionsContext,
  PpfpActionSummary,
  PpfpTaskGroup,
  PpfpTaskType,
  PpfpTaskSchedulePatch,
  PpfpTimelineContext,
} from '@completion/models';

type PpfpReducerAction = PpfpAction | ReturnType<(typeof PpfpActions)[keyof typeof PpfpActions]> | ChangeProject | ProjectsLoadSuccess;

export interface PpfpState {
  timelineContext: PpfpTimelineContext | null;
  timelinePreviews: Record<number, PpfpTaskSchedulePatch>;
  projectId: number | null;
  packageActionsContext: PpfpActionsContext | null;
  packageActions: PpfpActionSummary[];
  tasks: PpfpTask[];
  tasksLoaded: boolean;
  ppfps: Ppfp[];
  ppfpsLoaded: boolean;
  procurementPackages: ProcurementPackage[];
  procurementPackagesLoaded: boolean;
  taskTypes: PpfpTaskType[];
  taskGroups: PpfpTaskGroup[];
  loading: PpfpResourceState;
  loadError: PpfpResourceState;
  taskFilters: PpfpTaskFilters;
  packageFilters: PpfpPackageFilters;
}

export interface PpfpResourceState {
  tasks: boolean;
  ppfps: boolean;
  procurementPackages: boolean;
  taskTypes: boolean;
  taskGroups: boolean;
}

const emptyResourceState: PpfpResourceState = {
  tasks: false,
  ppfps: false,
  procurementPackages: false,
  taskTypes: false,
  taskGroups: false,
};

export const initialState: PpfpState = {
  timelineContext: null,
  timelinePreviews: {},
  projectId: null,
  packageActionsContext: null,
  packageActions: [],
  tasks: [],
  tasksLoaded: false,
  ppfps: [],
  ppfpsLoaded: false,
  procurementPackages: [],
  procurementPackagesLoaded: false,
  taskTypes: [],
  taskGroups: [],
  loading: emptyResourceState,
  loadError: emptyResourceState,
  taskFilters: emptyPpfpTaskFilters,
  packageFilters: emptyPpfpPackageFilters,
};

export function ppfpReducer(state: PpfpState = initialState, action: PpfpReducerAction) {
  if (
    action.type === PpfpActions.loadTasks.type ||
    action.type === PpfpActions.loadPpfps.type ||
    action.type === PpfpActions.loadProcurementPackages.type ||
    action.type === PpfpActions.loadTaskTypes.type ||
    action.type === PpfpActions.loadTaskGroups.type
  ) {
    if (state.projectId !== action.projectId) state = { ...initialState, projectId: action.projectId };
  }

  switch (action.type) {
    case PpfpActions.setTimelineContext.type:
      if (state.timelineContext?.projectId === action.context?.projectId && state.timelineContext?.ppfpId === action.context?.ppfpId)
        return state;
      return { ...state, timelineContext: action.context, timelinePreviews: {} };
    case PpfpActions.previewTaskScheduleAccepted.type:
      if (
        state.projectId !== action.projectId ||
        state.timelineContext?.projectId !== action.projectId ||
        state.timelineContext?.ppfpId !== action.ppfpId ||
        !state.tasks.some((task) => task.id === action.taskId && (action.ppfpId === null || task.ppfpId === action.ppfpId))
      )
        return state;
      return {
        ...state,
        timelinePreviews: {
          ...state.timelinePreviews,
          [action.taskId]: { ...state.timelinePreviews[action.taskId], ...action.changes },
        },
      };
    case PpfpActions.loadPackageActions.type:
      if (state.projectId !== action.projectId) return state;
      return {
        ...state,
        packageActionsContext: {
          projectId: action.projectId,
          procurementPackageId: action.procurementPackageId,
          ppfpId: action.ppfpId,
        },
        packageActions: [],
      };
    case PpfpActions.loadPackageActionsSuccess.type:
    case PpfpActions.loadPackageActionsFailure.type:
      if (
        state.projectId !== action.projectId ||
        state.packageActionsContext?.ppfpId !== action.ppfpId ||
        state.packageActionsContext?.procurementPackageId !== action.procurementPackageId
      ) {
        return state;
      }
      return {
        ...state,
        packageActions: action.type === PpfpActions.loadPackageActionsSuccess.type ? action.actions : [],
      };
    case PpfpActions.clearPackageActions.type:
      return { ...state, packageActionsContext: null, packageActions: [] };
    case ProjectActionTypes.ChangeProject:
    case ProjectActionTypes.ProjectsLoadSuccess: {
      const projectId = (action.type === ProjectActionTypes.ChangeProject ? action.project?.id : action.currentProject?.id) ?? null;
      return state.projectId === projectId ? state : { ...initialState, projectId };
    }
    case PpfpActions.clearTasks.type:
      return {
        ...state,
        timelinePreviews: {},
        tasks: [],
        tasksLoaded: false,
        loading: { ...state.loading, tasks: false },
        loadError: { ...state.loadError, tasks: false },
      };
    case PpfpActions.setTaskUsers.type:
      if (state.projectId !== action.projectId) return state;
      return {
        ...state,
        tasks: state.tasks.map((task) => (task.id === action.taskId ? { ...task, userIds: action.userIds } : task)),
      };
    case PpfpActions.setTaskMetadata.type:
      if (state.projectId !== action.projectId) return state;
      return {
        ...state,
        tasks: state.tasks.map((task) =>
          task.id === action.taskId
            ? {
                ...task,
                ...action.taskMetadata,
                ...(action.taskMetadata.selectedLocation === null
                  ? { plannedVisitOsmAddress: null }
                  : action.osmAddress !== undefined
                    ? { plannedVisitOsmAddress: action.osmAddress }
                    : {}),
              }
            : task,
        ),
      };
    case PpfpActions.applyTaskFilters.type:
      return {
        ...state,
        taskFilters: normalizePpfpTaskFilters(action.filters),
      };
    case PpfpActions.applyPackageFilters.type:
      return {
        ...state,
        packageFilters: normalizePpfpPackageFilters(action.filters),
      };
    case PpfpActions.loadTasks.type:
      return {
        ...state,
        loading: { ...state.loading, tasks: true },
        loadError: { ...state.loadError, tasks: false },
      };
    case PpfpActions.loadPpfps.type:
      return {
        ...state,
        loading: { ...state.loading, ppfps: true },
        loadError: { ...state.loadError, ppfps: false },
      };
    case PpfpActions.loadProcurementPackages.type:
      return {
        ...state,
        loading: { ...state.loading, procurementPackages: true },
        loadError: { ...state.loadError, procurementPackages: false },
      };
    case PpfpActions.loadTaskTypes.type:
      return {
        ...state,
        loading: { ...state.loading, taskTypes: true },
        loadError: { ...state.loadError, taskTypes: false },
      };
    case PpfpActions.loadTaskGroups.type:
      return {
        ...state,
        loading: { ...state.loading, taskGroups: true },
        loadError: { ...state.loadError, taskGroups: false },
      };
    case PpfpActions.loadTasksSuccess.type:
    case PpfpActionTypes.LoadPpfpTasksSuccess:
      return {
        ...state,
        timelinePreviews: {},
        tasks: action.tasks,
        tasksLoaded: true,
        loading: { ...state.loading, tasks: false },
      };
    case PpfpActions.loadTasksFailure.type:
    case PpfpActionTypes.LoadPpfpTasksFailure:
      return {
        ...state,
        timelinePreviews: {},
        tasks: [],
        tasksLoaded: false,
        loading: { ...state.loading, tasks: false },
        loadError: { ...state.loadError, tasks: true },
      };
    case PpfpActionTypes.LoadPpfpsSuccess:
      return {
        ...state,
        ppfps: action.ppfps,
        ppfpsLoaded: true,
        loading: { ...state.loading, ppfps: false },
      };
    case PpfpActionTypes.LoadPpfpsFailure:
      return {
        ...state,
        ppfps: [],
        ppfpsLoaded: false,
        loading: { ...state.loading, ppfps: false },
        loadError: { ...state.loadError, ppfps: true },
      };
    case PpfpActionTypes.LoadPpfpProcurementPackagesSuccess:
      return {
        ...state,
        procurementPackages: action.procurementPackages,
        procurementPackagesLoaded: true,
        loading: { ...state.loading, procurementPackages: false },
      };
    case PpfpActionTypes.LoadPpfpProcurementPackagesFailure:
      return {
        ...state,
        procurementPackages: [],
        procurementPackagesLoaded: false,
        loading: { ...state.loading, procurementPackages: false },
        loadError: { ...state.loadError, procurementPackages: true },
      };
    case PpfpActionTypes.LoadPpfpTaskTypesSuccess:
      return {
        ...state,
        taskTypes: action.taskTypes,
        loading: { ...state.loading, taskTypes: false },
      };
    case PpfpActionTypes.LoadPpfpTaskTypesFailure:
      return {
        ...state,
        taskTypes: [],
        loading: { ...state.loading, taskTypes: false },
        loadError: { ...state.loadError, taskTypes: true },
      };
    case PpfpActions.loadTaskGroupsSuccess.type:
    case PpfpActionTypes.LoadPpfpTaskGroupsSuccess:
      return {
        ...state,
        taskGroups: action.taskGroups,
        loading: { ...state.loading, taskGroups: false },
      };
    case PpfpActions.loadTaskGroupsFailure.type:
    case PpfpActionTypes.LoadPpfpTaskGroupsFailure:
      return {
        ...state,
        taskGroups: [],
        loading: { ...state.loading, taskGroups: false },
        loadError: { ...state.loadError, taskGroups: true },
      };
    default:
      return state;
  }
}
